import React, { useContext } from 'react';
import { mlb, internationalizeDate } from 'yankees-react-utils';
import { Row, Col, Card } from 'react-bootstrap';
import styled from 'styled-components';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePlayerGamesQuery, usePrefetchOktaQuery, Team, Player, useCachedOktaQuery } from '../../data';
import NavCard, { navCardHeaderHeight } from '../../components/NavCard';
import NavCardContainer from '../../components/NavCardContainer';
import SeriesVideoLink from './SeriesVideoLink';
import receiving from '../../images/receiving.jpg';
import strengths from '../../images/strengths.jpg';
import bullpen from '../../images/bullpen.png';
import cage from '../../images/cage.png';
import video from '../../images/video.jpg';
import myobjectives from '../../images/my-objectives.jpg';
import mechanicsandworkload from '../../images/mechanicsandworkload.jpg';
import GameLogs from '../../components/GameLogs';
import CalendarEvents from './CalendarEvents';
import OpponentCards from './OpponentsCard';
import { store, actions } from '../../store';
import PlayerHeadshot from '../../components/PlayerHeadshot';

const HomeContainer = styled.div`
    max-width: 1366px;
    width: 100%;
    margin-right: auto;
    margin-left: auto;
    display: flex;
    flex-direction: column;
    height: calc(100vh - 72px);
`;
const CardImage = styled.img`
    height: 80px;
    width: 120px;
    object-fit: cover;
`;
const BottomContent = styled.div`
    flex-grow: 1;
    @media (min-width: 992px) {
        overflow: hidden;
    }
`;
const StyledRow = styled(Row)`
    @media (min-width: 992px) {
        height: 100%;
        overflow: hidden;
    }
`;
const StyledCol = styled(Col)`
    margin-bottom: 10px;
    @media (min-width: 992px) {
        height: 100%;
        overflow: auto;
        margin-bottom: 0;
    }
`;

const SeriesCard = ({ teamId, isCurrentOpponent, ...rest }) => {
    const { dispatch } = useContext(store);
    const onSetOpponentTeam = async (data) => {
        if (!isCurrentOpponent) return;
        dispatch({ type: actions.SET_OPPONENT, data });
        dispatch({ type: actions.SET_MATCHUP_SELECTED_PITCHER, data: null });
    };
    const extraRosterQueryParams = window.$IsDugoutApp ? {} : { username: 'team_roster_lineups' };
    const rosterQueryKey = window.$IsDugoutApp ? 'dugout_roster' : 'team_roster';
    usePrefetchOktaQuery(['team', teamId], (accessToken) => Team.get(accessToken, teamId), { onSuccess: (data) => onSetOpponentTeam(data) });
    usePrefetchOktaQuery(['teamRoster', teamId, 'pitcher', rosterQueryKey], (t) => Player.list(t, { teamId, position: 'pitcher', ...extraRosterQueryParams }), {}, 1000 * 60 * 5);
    usePrefetchOktaQuery(['teamRoster', teamId, 'batter', rosterQueryKey], (t) => Player.list(t, { teamId, position: 'batter', ...extraRosterQueryParams }), {}, 1000 * 60 * 5);
    const extraQueryParams = window.$IsDugoutApp ? {} : { includeNonVisible: false };
    const linueupQueryKey = window.$IsDugoutApp ? 'dugout_lineup' : 'team_lineup';
    const { data: pitlineup } = useCachedOktaQuery(['lineup', teamId, 'pitcher', linueupQueryKey], (t) => Team.lineup(t, teamId, false, { ...extraQueryParams }),
        { }, 1000 * 60 * 5);
    const { data: batlineup } = useCachedOktaQuery(['lineup', teamId, 'batter', linueupQueryKey], (t) => Team.lineup(t, teamId, true, { ...extraQueryParams }),
        { }, 1000 * 60 * 5);
    return (
        <>
            <NavCard {...rest} />
            {isCurrentOpponent && (
                <div style={{ display: 'none' }}>
                    {pitlineup?.map(({ PlayerID }) => <PlayerHeadshot key={PlayerID} playerId={PlayerID} className="fa fa-fallback" />)}
                    {batlineup?.map(({ PlayerID }) => <PlayerHeadshot key={PlayerID} playerId={PlayerID} className="fa fa-fallback" />)}
                </div>
            )}
        </>
    );
};

const DSL_LEAGUE_ID = 21;

const Home = () => {
    const { t: translate } = useTranslation();
    const { PlayerID, IsPitcher, IsCatcher, series, TeamID, Team: ContextTeam } = usePlayerGamesQuery();
    const { state: { user: loggedInUser } } = useContext(store);
    const userCanViewOpponents = ContextTeam.LeagueID !== DSL_LEAGUE_ID && loggedInUser.Roles?.some((userRole) => ['affiliateanalyst', 'mjcoach', 'mncoach', 'nyfrontoffice', 'playerdev', 'videomanager', 'viewmjadvance', 'viewmnadvance', 'player'].includes(userRole.RoleName));

    const seriesCards = series?.map((s) => ({
        teamId: s.TeamID,
        // eslint-disable-next-line no-nested-ternary
        header: `${s.isPreviousSeries ? translate('home.series-previous') : s.isCurrentSeries ? translate('home.series-current') : translate('home.series-upcoming')}`,
        title: `${s.isHomeSeries ? '' : '@'} ${s.City?.length > 0 ? s.City : s.Name}`,
        subTitle: `${internationalizeDate(translate, new Date(s.startDate), false, false, true, true, false, false, false, false)}${s.startDate !== s.endDate ? ` - ${internationalizeDate(translate, new Date(s.endDate), false, false, true, true, false, false, false, false)}` : ''}`,
        route: `/opponent/${s.TeamID}`,
        imgSrc: `/api/team/${s.TeamID}/logo`,
        imgBackground: mlb.backgrounds[s.TeamID],
        height: 150,
        children: s.games.map((g) => <SeriesVideoLink key={g.GameID} game={g} isHomeSeries={s.isHomeSeries} isPitcher={IsPitcher} />),
        childrenStyle: {
            right: 0,
            top: navCardHeaderHeight,
            height: `calc(100% - ${navCardHeaderHeight})`,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
        },
        lg: 6,
        xl: 6,
    }));
    const reportCards = [];
    if (userCanViewOpponents) {
        reportCards.push(
            { title: translate('home.opponents'), component: () => <OpponentCards key="opponent-card" /> },
        );
    }
    if (IsCatcher) {
        reportCards.push(
            { title: translate('home.strengths-catcher'), route: `/catcher-strengths/${PlayerID}`, imgSrc: receiving },
        );
    }
    if (IsPitcher || IsCatcher) {
        reportCards.push(
            { title: translate('home.strengths-pitcher'), route: '/pitcher-strengths', imgSrc: strengths, imgBackground: 'transparent' },
        );
    }
    if (!IsPitcher) {
        reportCards.push(
            { title: translate('home.strengths-hitter'), route: '/hitter-strengths', imgSrc: strengths, imgBackground: 'transparent' },
        );
    }
    reportCards.push(
        { title: translate('home.video-playlists'), route: '/video-playlists/my-playlists', imgSrc: video },
        { title: translate('home.my-objectives'), route: '/my-objectives', imgSrc: myobjectives },
        {
            title: IsPitcher ? translate('home.sessions-bullpen') : translate('home.sessions-cage'),
            route: '/ballcap',
            imgSrc: IsPitcher ? bullpen : cage,
            imgBackground: 'transparent',
        },
        { title: translate('home.workload-mechanics'), route: '/mechanics-and-workload', imgSrc: mechanicsandworkload },
    );

    return (
        <HomeContainer>
            {!!seriesCards?.length && userCanViewOpponents && (
                <NavCardContainer>
                    {seriesCards.map((tab, ix) => <SeriesCard key={tab.title} isCurrentOpponent={ix === 0} {...tab} />)}
                </NavCardContainer>
            )}
            <BottomContent>
                <StyledRow>
                    <StyledCol lg={4}>
                        <CalendarEvents teamId={TeamID} playerId={PlayerID} isPitcher={IsPitcher} isCatcher={IsCatcher} />
                    </StyledCol>
                    <StyledCol lg={4}>
                        <Card className="px-3 pb-3 shadow-sm pt-2">
                            <GameLogs playerId={PlayerID} isPitcher={IsPitcher} isCatcher={IsCatcher} />
                        </Card>
                    </StyledCol>
                    <StyledCol lg={4}>
                        {reportCards.map((rp) => (rp.component ? rp.component(series) : (
                            <NavLink to={rp.route} className="shadow-sm border mb-2 rounded d-flex bg-white overflow-hidden" key={rp.route}>
                                <CardImage src={rp.imgSrc} alt={rp.title} />
                                <div className="d-flex align-items-center px-4">
                                    <h4 className="mb-0">{rp.title}</h4>
                                </div>
                            </NavLink>
                        )))}
                    </StyledCol>
                </StyledRow>
            </BottomContent>
        </HomeContainer>
    );
};

Home.propTypes = {

};

export default Home;
